import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useProductionKPIs } from "@/hooks/use-production-kpis";
import { useAnimatedCounter } from "@/hooks/use-animated-counter";
import { useTodaysBirthdays } from "@/hooks/use-birthdays";
import { CircularProgress } from "@/components/display/CircularProgress";
import { TVFooter } from "@/components/display/TVFooter";
import logoWhite from "@/assets/logo-white.svg";
import scribbleUnderline from "@/assets/scribble/vektor_skribble_sølvrev_brushscribble-1.svg";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

const PHASE_DISPLAY: Record<string, { label: string; bg: string; text: string }> = {
  preprod: { label: "Preproduksjon", bg: "bg-purple-100", text: "text-purple-700" },
  production: { label: "Produksjon", bg: "bg-blue-100", text: "text-blue-700" },
  postprod: { label: "Postproduksjon", bg: "bg-emerald-100", text: "text-emerald-700" },
  faktura: { label: "Faktura", bg: "bg-amber-100", text: "text-amber-700" },
};

export default function DisplayProductionPage() {
  const { data, isLoading } = useProductionKPIs();
  const { data: birthdays } = useTodaysBirthdays();

  const animatedHours = useAnimatedCounter(data?.totalHoursThisMonth ?? 0, { duration: 1200 });
  const animatedProjects = useAnimatedCounter(data?.activeProjects ?? 0, { duration: 800 });
  const animatedBudget = useAnimatedCounter(data?.totalBudgetedHours ?? 0, { duration: 1000 });
  const hoursProgress = data && data.totalBudgetedHours > 0
    ? Math.round((data.totalHoursThisMonth / data.totalBudgetedHours) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <SrIcon name="loader2" className="w-8 h-8 animate-spin text-muted-foreground"  />
      </div>
    );
  }

  const consultants = data?.consultantStats ?? [];
  const phases = data?.projectPhases ?? [];

  return (
    <div className="h-screen flex flex-col p-4 gap-3 overflow-hidden bg-background">
      {/* Header with brand */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <img src={logoWhite} alt="" className="w-7 h-7 invert" />
          <div className="relative">
            <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] text-foreground tracking-wide flex items-center gap-2">
              <SrIcon name="monitor" className="h-6 w-6"  />
              Produksjon
            </h1>
            <img src={scribbleUnderline} alt="" className="absolute -bottom-1 left-0 w-full h-2 opacity-20" />
          </div>
        </div>
        <span className="font-display text-sm text-muted-foreground tracking-wider">
          {new Date().toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" })}
        </span>
      </div>

      {birthdays && birthdays.length > 0 && (
        <Card className="animate-fade-in bg-pink-50 border-pink-200">
          <CardContent className="p-3 flex items-center gap-3">
            <SrIcon name="cake" className="h-5 w-5 text-pink-600 shrink-0"  />
            <p className="text-sm font-medium text-pink-900">
              Gratulerer med dagen, {birthdays.map((b) => b.full_name).join(", ")}! 🎂
            </p>
          </CardContent>
        </Card>
      )}

      {/* KPI Row */}
      <Card className="animate-fade-in">
        <CardContent className="p-4">
          <div className="grid gap-3 grid-cols-4">
            <div className="p-4 rounded-lg border border-border/50 bg-card/50 flex flex-col items-center">
              <p className="text-xs font-medium text-muted-foreground mb-2">Faktureringsrate team</p>
              <CircularProgress value={data?.teamBillingRate ?? 0} size={90} />
            </div>

            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-muted-foreground">Timer denne måneden</p>
                <SrIcon name="clock" className="h-4 w-4 text-blue-500"  />
              </div>
              <div className="text-3xl font-bold tabular-nums">
                {animatedHours}
                <span className="text-lg text-muted-foreground">/{animatedBudget}</span>
              </div>
              <Progress value={Math.min(hoursProgress, 100)} className="mt-2 h-2" />
            </div>

            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-muted-foreground">Aktive prosjekter</p>
                <SrIcon name="folder-kanban" className="h-4 w-4 text-emerald-500"  />
              </div>
              <div className="text-3xl font-bold tabular-nums">{animatedProjects}</div>
              <p className="mt-1 text-xs text-muted-foreground">prosjekter pågår</p>
            </div>

            <div className="p-4 rounded-lg border border-border/50 bg-card/50">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-muted-foreground">Ansatte med timer</p>
                <SrIcon name="trophy" className="h-4 w-4 text-amber-500"  />
              </div>
              <div className="text-3xl font-bold tabular-nums">{consultants.length}</div>
              <p className="mt-1 text-xs text-muted-foreground">har ført timer denne mnd</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main content */}
      <div className="grid gap-3 grid-cols-2 flex-1 min-h-0">
        {/* Consultant leaderboard */}
        <Card className="animate-fade-in flex flex-col">
          <CardContent className="p-4 flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-base flex items-center gap-2">
                <SrIcon name="trophy" className="h-4 w-4 text-emerald-500"  />
                Konsulent-toppliste
              </h2>
            </div>
            <div className="space-y-2 overflow-auto flex-1">
              {consultants.slice(0, 8).map((c, index) => {
                const isTop = index === 0;
                const initials = c.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2);
                return (
                  <div
                    key={c.user_id}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border border-border/50 transition-all",
                      isTop ? "bg-primary/10 border-primary/30" : "bg-card/50"
                    )}
                  >
                    <div className="relative">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt={c.full_name} className={cn("h-10 w-10 rounded-full object-cover", isTop && "ring-2 ring-primary")} />
                      ) : (
                        <span className={cn("h-10 w-10 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center", isTop && "ring-2 ring-primary")}>{initials}</span>
                      )}
                      <div className={cn("absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold", isTop ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate">{c.full_name}</h3>
                        {isTop && <span className="text-base">🏆</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {c.total_hours}t / {c.budgeted_hours}t
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={cn("font-medium text-sm", c.billing_rate >= 80 ? "text-emerald-600" : "text-muted-foreground")}>
                        {c.billing_rate}%
                      </span>
                      <p className="text-[10px] text-muted-foreground">fakt.rate</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Project phase status */}
        <Card className="animate-fade-in flex flex-col">
          <CardContent className="p-4 flex-1 flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-3">
              <SrIcon name="folder-kanban" className="h-4 w-4 text-muted-foreground"  />
              <h2 className="font-display text-base">Prosjektstatus per fase</h2>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left py-2 px-1 text-xs font-medium text-muted-foreground">Prosjekt</th>
                    <th className="text-center py-2 px-1 text-xs font-medium text-muted-foreground w-32">Fase</th>
                  </tr>
                </thead>
                <tbody>
                  {phases.slice(0, 12).map((p) => {
                    const display = PHASE_DISPLAY[p.project_phase ?? ""] ?? { label: p.project_phase ?? "—", bg: "bg-gray-100", text: "text-gray-600" };
                    return (
                      <tr key={p.project_id} className="border-b border-border/30 last:border-0">
                        <td className="py-2 px-1">
                          <span className="text-xs text-primary tabular-nums mr-1">{p.project_code}</span>
                          <span className="text-sm truncate">{p.project_name}</span>
                        </td>
                        <td className="py-2 px-1 text-center">
                          <span className={cn("px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap", display.bg, display.text)}>
                            {display.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <TVFooter variant="light" />
    </div>
  );
}
